import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { memberApi } from "../lib/memberApi";

// Wrap a promise with a timeout — prevents Render cold-start from hanging forever
function withTimeout(promise, ms) {
  const timer = new Promise((_, reject) =>
    setTimeout(() => reject(new Error("Request timed out")), ms)
  );
  return Promise.race([promise, timer]);
}
const AuthContext = createContext();
const MEMBER_DRAFT_PROFILE_KEY = "ospf_member_draft_profile";
const clearMemberDraftProfile = () => {
  try {
    localStorage.removeItem(MEMBER_DRAFT_PROFILE_KEY);
  } catch { }
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  // ===============================
  // LOAD EXISTING SESSION
  // ===============================
  const boot = useCallback(async () => {
    setLoading(true);

    try {
      const adminToken = localStorage.getItem("ospf_admin_token");
      const memberToken = localStorage.getItem("ospf_member_token");

      // ADMIN SESSION
      if (adminToken) {
        try {
          const { data } = await api.get("/admin/me", {
            headers: {
              Authorization: `Bearer ${adminToken}`,
            },
          });

          setAdmin(data.admin || data.user || data);
        } catch {
          localStorage.removeItem("ospf_admin_token");
          setAdmin(null);
        }
      }

      // MEMBER SESSION — use a timeout to survive Render cold-start delays
      if (memberToken) {
        try {
          const { data } = await withTimeout(
            api.get("/members/me", {
              headers: { Authorization: `Bearer ${memberToken}` },
            }),
            15000 // 15 s — enough for Render dyno wake + Mongo reconnect
          );

          setMember(data.member || data);
        } catch (e) {
          const status = e?.response?.status;
          // Only evict the token on explicit auth failure (401/403).
          // A 5xx or timeout means the backend is waking up — keep the token
          // so the user isn't silently logged out.
          if (status === 401 || status === 403) {
            localStorage.removeItem("ospf_member_token");
            setMember(null);
          }
          // On 5xx / timeout: member stays null, loading will be set false below.
          // ProtectedRoute will handle the retry prompt.
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    boot();
  }, [boot]);

  const refresh = async () => {
    await boot();
  };

  // ===============================
  // ADMIN LOGIN
  // ===============================
  const login = async (email, password) => {
    try {
      const { data } = await memberApi.post("/auth/login", {
        email,
        password,
      });

      localStorage.setItem("ospf_admin_token", data.token);

      setAdmin(data.admin || data.user || { email, role: "admin" });

      return { ok: true };
    } catch (e) {
      return {
        ok: false,
        error: formatApiError(e),
      };
    }
  };

  // ===============================
  // MEMBER PASSWORD LOGIN
  // ===============================
  const memberLogin = async (email, password, externalToken = null) => {
    try {
      let token;

      if (externalToken) {
        token = externalToken;
      } else {
        const { data } = await memberApi.post("/member/login", {
          email,
          password,
        });

        token = data.token;
      }

      // save token
      localStorage.setItem("ospf_member_token", token);

      // fetch member — single call, result is shared with the caller via return value
      const { data } = await memberApi.get("/members/me");

      const user = data.member || data;

      setMember(user);

      // Return the member so callers don't need to fetch /members/me again
      return { ok: true, member: user };
    } catch (e) {
      return {
        ok: false,
        error: e?.response?.data?.detail || "Login failed",
      };
    }
  };

  // ===============================
  // ADMIN LOGOUT
  // ===============================
  const logout = async () => {
    try {
      await memberApi.post("/auth/logout");
    } catch { }

    localStorage.removeItem("ospf_admin_token");
    setAdmin(null);
  };

  // ===============================
  // MEMBER LOGOUT
  // ===============================
  const memberLogout = async () => {
    try {
      await memberApi.post("/member/logout");
    } catch { }

    localStorage.clear();
    setMember(null);
  };

  return (
    <AuthContext.Provider
      value={{
        admin,
        member,
        loading,
        refresh,

        // admin
        login,
        logout,

        // member
        memberLogin,
        memberLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
